import { useEffect, useState } from "react";
import { dbx } from "src/utils/urlBack_End";

/**
 * Hook para asegurar que existe una carpeta en Dropbox
 * Verifica si la carpeta existe, y si no, la crea automáticamente
 *
 * @param {string} folderPath - Ruta de la carpeta en Dropbox (ej: "/Clientes/12345678-9")
 * @returns {Object} Estado de la carpeta
 * @returns {boolean} return.exists - true si la carpeta existe o fue creada
 * @returns {boolean} return.creating - true mientras se está creando la carpeta
 * @returns {Error|null} return.error - Error si falló la creación, null si todo OK
 *
 * @example
 * // Uso básico - crea carpeta del cliente
 * const { exists, creating, error } = useDropboxFolder(`/Clientes/${rut}`);
 *
 * @example
 * // Uso con subcarpeta
 * const { exists, creating, error } = useDropboxFolder(`/Clientes/${rut}/Contratos`);
 *
 * @example
 * // Con manejo de error
 * const { exists, creating, error } = useDropboxFolder(`/Clientes/${rut}`);
 * if (error) console.error("Error creando carpeta:", error);
 */
export const useDropboxFolder = (folderPath) => {
  const [status, setStatus] = useState({
    exists: false,
    creating: false,
    error: null,
  });

  useEffect(() => {
    // Si no hay ruta, no hacer nada
    if (!folderPath) return;

    setStatus((prev) => ({ ...prev, creating: true }));

    // Intentar listar la carpeta
    dbx
      .filesListFolder({ path: folderPath })
      .then(() => {
        // Carpeta existe
        setStatus({ exists: true, creating: false, error: null });
      })
      .catch(() => {
        // Carpeta no existe, intentar crearla
        dbx
          .filesCreateFolderV2({ path: folderPath })
          .then(() => {
            console.log(`Carpeta creada en Dropbox: ${folderPath}`);
            setStatus({ exists: true, creating: false, error: null });
          })
          .catch((error) => {
            console.error("Error al crear carpeta en Dropbox:", error);
            setStatus({ exists: false, creating: false, error });
          });
      });
  }, [folderPath]);

  return status;
};
